// requestsSearch (WEL-64, extended WEL-120) — the Requests-table free-text search
// predicate, in a dependency-free module so it stays unit-testable in isolation
// (no React import).
//
// Matches the query against EVERY text column the table shows — the type/kind
// (incl. "Renewal"), the applicants + subject, the recipient name/email/role, the
// unit + property + prior address, the group id/label, and the status — so staff
// can free-text filter the whole table: type "lease" and every lease shows, type a
// name and every row for that name shows, type a property and so on. The query is
// split on whitespace and EVERY word must match somewhere in the row (WEL-163:
// Andrea typed "35 Ash" and "35 NE Ash Ave" did not match, so a sent agreement
// looked missing). Each word is a case-insensitive REGEX (so `^108` or
// `lease|renewal` still work), falling back to a plain substring match when it
// isn't valid regex, so a stray `(` never breaks the box.

import type { OutboundRequestRow } from './requestsClient';

/** Just the row fields the search reads — keeps the predicate testable without a
 *  full OutboundRequestRow fixture. All optional: the predicate reads each field
 *  defensively (a real OutboundRequestRow satisfies this via structural typing). */
export type SearchableRequestRow = Partial<
  Pick<
    OutboundRequestRow,
    | 'kind'
    | 'kindLabel'
    | 'isRenewal'
    | 'recipientName'
    | 'recipientEmail'
    | 'recipientRole'
    | 'subjectLabel'
    | 'groupId'
    | 'groupLabel'
    | 'applicants'
    | 'unit'
    | 'property'
    | 'priorAddress'
    | 'statusLabel'
  >
>;

/** The concatenated searchable text of a row (every column the table renders). */
function haystackOf(row: SearchableRequestRow): string {
  return [
    row.kind, // the raw kind ('lease', 'rental_verification', …) so "lease" hits renewals too
    row.kindLabel,
    row.isRenewal ? 'Renewal' : undefined,
    row.recipientName,
    row.recipientEmail,
    row.recipientRole,
    row.subjectLabel,
    row.applicants,
    row.unit,
    row.property,
    // WEL-101: the RV row's prior-residence address (the applied-for property is
    // in the Property column now; keep the prior address findable explicitly).
    row.priorAddress,
    row.groupId,
    row.groupLabel,
    row.statusLabel,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * True iff `row` matches the (raw) free-text `query`. Empty query matches
 * everything. The query is applied as a case-insensitive regex against the row's
 * whole searchable text; an invalid regex degrades to a case-insensitive
 * substring match. Pure.
 */
export function matchesSearchQuery(row: SearchableRequestRow, query: string): boolean {
  const words = query.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = haystackOf(row);
  const lower = hay.toLowerCase();
  return words.every((w) => {
    try {
      return new RegExp(w, 'i').test(hay);
    } catch {
      return lower.includes(w.toLowerCase());
    }
  });
}
